import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { isSameWeek } from 'date-fns';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { useEventContext } from '../../../../../global-state/eventContext';
import { AppCustomersResponse, useAsyncAction, useTransaction } from '../../../../../hooks';
import { Container, Icon, Icons, Tooltip, Typography } from '../../../../../stories/atoms';
import { homeTranslations } from '../../../../../translations';
import { isFailureResponse } from '../../../../../utils';
import { InfoTooltipIcon } from '../../../common/infoTooltipIcon/InfoTooltipIcon';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { StyledSection } from './AppCustomers.styles';

export type AppCustomersProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
};

export const AppCustomers: FC<AppCustomersProps> = observer(({ interval, currentDate }) => {
  const { t } = useTranslation();
  const { appCustomers: appCustomersTranslations } = homeTranslations;

  const { fetchAppCustomersData } = useTransaction();
  const { transactionEventsCount } = useEventContext();

  const {
    result: appCustomerData,
    isLoading: appCustomerDataLoading,
    isError: appCustomerDataError,
    triggerAction: triggerFetchAppCustomerData,
  } = useAsyncAction<AppCustomersResponse>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchAppCustomersData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [interval, currentDate]);

  const fetchWidgetData = () => {
    triggerFetchAppCustomerData(fetchData);
  };

  useEffect(() => {
    fetchWidgetData();
  }, [currentDate, transactionEventsCount]);

  const getAppCustomerPercentage = () => {
    if (!appCustomerData) return 0;

    return appCustomerData?.activeCustomers === 0
      ? 0
      : (appCustomerData?.appCustomers / appCustomerData?.activeCustomers) * 100;
  };

  const isCurrentWeek = isSameWeek(currentDate, new Date(), { weekStartsOn: 0 });

  const isNoData =
    appCustomerData?.activeCustomers === 0 &&
    appCustomerData.appCustomers === 0 &&
    appCustomerData.newAppCustomers === 0;

  return (
    <WidgetCardContent
      headerIcon={Icons.CUSTOMERVALUE}
      headerText={t(appCustomersTranslations.title.key, appCustomersTranslations.title.defaultValue)}
      subHeaderText={t(appCustomersTranslations.subTitle.key, appCustomersTranslations.subTitle.defaultValue)}
      toolTipText={t(appCustomersTranslations.toolTip.key, appCustomersTranslations.toolTip.defaultValue)}
      isLoading={appCustomerDataLoading}
      isError={appCustomerDataError}
      errorText={t(appCustomersTranslations.error.key, appCustomersTranslations.error.defaultValue)}
      errorRetryHandler={fetchWidgetData}
      isNoData={isNoData}
      lastUpdatedDate={appCustomerData?.lastUpdated}
      content={
        <>
          <Container wrap="nowrap" padding={[3, 4]} data-testid="app-customers-wrapper" height="100%">
            <Container wrap="nowrap" direction="horizontal" margin={[0, 0, 2]}>
              <Icon data-testid="mobileIcon" icon={Icons.NEWMEMBER} margin={[0, 2, 0, 0]} />
              <Typography type="button">
                {t(
                  appCustomersTranslations.newCustomersText.key,
                  appCustomersTranslations.newCustomersText.defaultValue,
                )}
              </Typography>
              {isCurrentWeek && (
                <Tooltip
                  position="right-end"
                  arrowedToolTip
                  text={
                    <Container padding={[2]}>
                      <Typography type="legal" color="white">
                        {t(
                          appCustomersTranslations.currentWeekNote.key,
                          appCustomersTranslations.currentWeekNote.defaultValue,
                        )}
                      </Typography>
                    </Container>
                  }
                >
                  <Icon icon={Icons.INFO} margin={[1, 0, 0, 1]} size="small" />
                </Tooltip>
              )}
            </Container>

            <Typography type="caption">
              <Trans
                i18nKey={appCustomersTranslations.newAppCustomerSubtext.key}
                defaults={appCustomersTranslations.newAppCustomerSubtext.defaultValue}
                components={{ b: <b /> }}
              />
            </Typography>
            <Container wrap="nowrap" padding={[2, 0, 0]} style={{ gap: '8px', flex: 1 }}>
              <StyledSection
                padding={[2]}
                wrap="nowrap"
                space="between"
                position="center"
                direction="horizontal"
                data-testid="app-achievers"
              >
                <Container wrap="nowrap" direction="horizontal" position="center">
                  <Icon data-testid="mobileIcon" icon={Icons.STAR} margin={[0, 2, 0, 0]} />
                  <Typography>
                    {t(
                      appCustomersTranslations.appAchieversText.key,
                      appCustomersTranslations.appAchieversText.defaultValue,
                    )}
                  </Typography>
                  <InfoTooltipIcon
                    tooltipText={t(
                      appCustomersTranslations.tooltips.appAchieversTooltip.key,
                      appCustomersTranslations.tooltips.appAchieversTooltip.defaultValue,
                    )}
                    testId="app-achievers-info-icon"
                  />
                </Container>
                <Typography type="button">{appCustomerData?.newAppCustomers.toFixed()}</Typography>
              </StyledSection>

              <StyledSection
                padding={[2]}
                wrap="nowrap"
                space="between"
                position="center"
                direction="horizontal"
                data-testid="new-app-customers"
              >
                <Container wrap="nowrap" direction="horizontal" position="center">
                  <Icon data-testid="mobileIcon" icon={Icons.CONVERSION} margin={[0, 2, 0, 0]} />
                  <Typography>
                    {t(appCustomersTranslations.title.key, appCustomersTranslations.title.defaultValue)}
                  </Typography>
                  <InfoTooltipIcon
                    tooltipText={t(
                      appCustomersTranslations.tooltips.appCustomersTooltip.key,
                      appCustomersTranslations.tooltips.appCustomersTooltip.defaultValue,
                    )}
                    testId="new-app-customers-info-icon"
                  />
                </Container>
                <Typography type="button">{getAppCustomerPercentage().toFixed(2)}</Typography>
              </StyledSection>

              <StyledSection
                padding={[2]}
                wrap="nowrap"
                space="between"
                position="center"
                direction="horizontal"
                data-testid="active-customers"
              >
                <Container wrap="nowrap" direction="horizontal" position="center">
                  <Icon data-testid="mobileIcon" icon={Icons.LOYALTYCUSTOMER} margin={[0, 2, 0, 0]} />
                  <Typography>
                    {t(
                      appCustomersTranslations.activeCustomersText.key,
                      appCustomersTranslations.activeCustomersText.defaultValue,
                    )}
                  </Typography>
                  <InfoTooltipIcon
                    tooltipText={t(
                      appCustomersTranslations.tooltips.activeCustomersTooltip.key,
                      appCustomersTranslations.tooltips.activeCustomersTooltip.defaultValue,
                    )}
                    testId="active-customers-info-icon"
                  />
                </Container>
                <Typography type="button">{appCustomerData?.activeCustomers.toFixed()}</Typography>
              </StyledSection>

              <StyledSection
                padding={[2]}
                wrap="nowrap"
                space="between"
                position="center"
                direction="horizontal"
                data-testid="app-customers"
              >
                <Container wrap="nowrap" direction="horizontal" position="center">
                  <Icon data-testid="mobileIcon" icon={Icons.MOBILE} margin={[0, 2, 0, 0]} />
                  <Typography>
                    {t(
                      appCustomersTranslations.appCustomerText.key,
                      appCustomersTranslations.appCustomerText.defaultValue,
                    )}
                  </Typography>
                  <InfoTooltipIcon
                    tooltipText={t(
                      appCustomersTranslations.tooltips.appCustomerTooltip.key,
                      appCustomersTranslations.tooltips.appCustomerTooltip.defaultValue,
                    )}
                    testId="app-customers-info-icon"
                  />
                </Container>
                <Typography type="button">{appCustomerData?.appCustomers.toFixed()}</Typography>
              </StyledSection>
            </Container>
          </Container>
        </>
      }
    />
  );
});
